// src/utils/reportingUtils.js

/**
 * Reporting Utilities for Production Management
 */

/**
 * Generate production status report data
 * @param {Array} productionOrders - Production orders
 * @returns {Object} Report data
 */
export const generateProductionStatusReport = (productionOrders) => {
    const report = {
        summary: {
            totalOrders: productionOrders.length,
            planned: 0,
            inProgress: 0,
            completed: 0,
            cancelled: 0
        },
        orders: []
    };

    productionOrders.forEach(order => {
        // Update summary
        report.summary[order.status] = (report.summary[order.status] || 0) + 1;

        // Add order details
        report.orders.push({
            id: order.id,
            product: order.product,
            recipe: order.recipeName,
            quantity: order.quantity,
            completed: order.completedQuantity,
            status: order.status,
            priority: order.priority,
            startDate: order.startDate,
            endDate: order.endDate,
            assignedTo: order.assignedTo || 'Atanmamış',
            progress: order.quantity > 0 ? Math.round((order.completedQuantity / order.quantity) * 100) : 0
        });
    });

    return report;
};

/**
 * Generate raw material stock report data
 * @param {Array} rawMaterials - Raw materials
 * @param {Array} warehouses - Warehouses
 * @returns {Object} Report data
 */
export const generateRawMaterialStockReport = (rawMaterials, warehouses) => {
    const report = {
        summary: {
            totalMaterials: rawMaterials.length,
            criticalLevel: 0,
            outOfStock: 0
        },
        materials: []
    };

    rawMaterials.forEach(material => {
        const totalStock = material.warehouseQuantities ?
            Object.values(material.warehouseQuantities).reduce((sum, qty) => sum + (qty || 0), 0) :
            (material.quantity || 0);

        const isCritical = totalStock <= (material.criticalLevel || 0);
        const isOutOfStock = totalStock <= 0;

        if (isCritical) report.summary.criticalLevel++;
        if (isOutOfStock) report.summary.outOfStock++;

        // Get warehouse details
        const warehouseDetails = material.warehouseQuantities ?
            Object.entries(material.warehouseQuantities)
                .map(([warehouseId, quantity]) => {
                    const warehouse = warehouses.find(w => w.id === warehouseId);
                    return {
                        warehouseName: warehouse ? warehouse.name : 'Bilinmeyen Depo',
                        quantity: quantity || 0,
                        unit: material.unit || 'Adet'
                    };
                })
                .filter(item => item.quantity > 0) : [];

        report.materials.push({
            id: material.id,
            name: material.name,
            code: material.code,
            totalStock,
            unit: material.unit || 'Adet',
            criticalLevel: material.criticalLevel || 0,
            status: isOutOfStock ? 'Stokta Yok' : isCritical ? 'Kritik Seviye' : 'Yeterli Stok',
            warehouseDetails
        });
    });

    return report;
};

/**
 * Generate subcontractor production report data
 * @param {Array} subcontractors - Subcontractors
 * @param {Array} subcontractingOrders - Subcontracting orders
 * @returns {Object} Report data
 */
export const generateSubcontractorProductionReport = (subcontractors, subcontractingOrders) => {
    const report = {
        summary: {
            totalSubcontractors: subcontractors.length,
            totalOrders: subcontractingOrders.length,
            completedOrders: 0,
            inProgressOrders: 0
        },
        subcontractors: []
    };

    subcontractors.forEach(subcontractor => {
        const subcontractorOrders = subcontractingOrders.filter(
            order => order.supplierId === subcontractor.id
        );

        const completedOrders = subcontractorOrders.filter(
            order => order.status === 'Delivered'
        ).length;

        const inProgressOrders = subcontractorOrders.filter(
            order => order.status === 'In Progress' || order.status === 'Ordered'
        ).length;

        report.summary.completedOrders += completedOrders;
        report.summary.inProgressOrders += inProgressOrders;

        const totalOrderValue = subcontractorOrders.reduce(
            (sum, order) => sum + (order.cost || 0), 0
        );

        report.subcontractors.push({
            id: subcontractor.id,
            name: subcontractor.name,
            totalOrders: subcontractorOrders.length,
            completedOrders,
            inProgressOrders,
            totalOrderValue,
            productionTypes: subcontractor.productionTypes || []
        });
    });

    return report;
};

/**
 * Generate subcontractor balance report data
 * @param {Array} subcontractors - Subcontractors
 * @param {Array} payments - Payments
 * @returns {Object} Report data
 */
export const generateSubcontractorBalanceReport = (subcontractors, payments) => {
    const report = {
        summary: {
            totalSubcontractors: subcontractors.length,
            totalBalance: 0,
            totalPayments: 0
        },
        subcontractors: []
    };

    subcontractors.forEach(subcontractor => {
        const subcontractorPayments = payments.filter(
            payment => payment.supplierId === subcontractor.id
        );

        const totalPayments = subcontractorPayments.reduce(
            (sum, payment) => sum + (payment.amount || 0), 0
        );

        const balance = subcontractor.balance || 0;

        report.summary.totalBalance += balance;
        report.summary.totalPayments += totalPayments;

        report.subcontractors.push({
            id: subcontractor.id,
            name: subcontractor.name,
            balance,
            totalPayments,
            outstandingBalance: balance - totalPayments
        });
    });

    return report;
};

/**
 * Generate product cost analysis report data
 * @param {Array} products - Products with cost information
 * @returns {Object} Report data
 */
export const generateProductCostAnalysisReport = (products) => {
    const report = {
        summary: {
            totalProducts: products.length,
            averageCost: 0,
            highestCostProduct: null,
            lowestCostProduct: null
        },
        products: []
    };

    let totalCost = 0;
    let highestCost = 0;
    let lowestCost = Infinity;
    let highestCostProduct = null;
    let lowestCostProduct = null;

    products.forEach(product => {
        const unitCost = product.unitCost?.total || 0;
        totalCost += unitCost;

        if (unitCost > highestCost) {
            highestCost = unitCost;
            highestCostProduct = product;
        }

        if (unitCost < lowestCost && unitCost > 0) {
            lowestCost = unitCost;
            lowestCostProduct = product;
        }

        report.products.push({
            id: product.id,
            name: product.name,
            code: product.code,
            unitCost,
            salePrice: product.salePrice || 0,
            profitMargin: product.profitMargin?.percentage || 0,
            rawMaterialCost: product.unitCost?.rawMaterials || 0,
            subcontractingCost: product.unitCost?.subcontracting || 0,
            laborCost: product.unitCost?.labor || 0,
            overheadCost: product.unitCost?.overhead || 0
        });
    });

    report.summary.averageCost = products.length > 0 ? totalCost / products.length : 0;
    report.summary.highestCostProduct = highestCostProduct;
    report.summary.lowestCostProduct = lowestCostProduct;

    return report;
};

/**
 * Generate waste report data
 * @param {Array} productionOrders - Production orders with waste data
 * @returns {Object} Report data
 */
export const generateWasteReport = (productionOrders) => {
    const report = {
        summary: {
            totalOrders: 0,
            totalWaste: 0,
            averageWasteRate: 0
        },
        orders: []
    };

    let totalWaste = 0;
    let totalPlanned = 0;

    productionOrders.forEach(order => {
        if (order.wasteData) {
            report.summary.totalOrders++;
            totalWaste += order.wasteData.totalWaste || 0;
            totalPlanned += order.wasteData.totalPlanned || 0;

            report.orders.push({
                id: order.id,
                product: order.product,
                recipe: order.recipeName,
                totalPlanned: order.wasteData.totalPlanned || 0,
                totalWaste: order.wasteData.totalWaste || 0,
                wasteRate: order.wasteData.wasteRate || 0,
                stages: order.wasteData.stages || []
            });
        }
    });

    report.summary.totalWaste = totalWaste;
    report.summary.averageWasteRate = totalPlanned > 0 ? (totalWaste / totalPlanned) * 100 : 0;

    return report;
};

/**
 * Export report data to CSV format
 * @param {Object} reportData - Report data
 * @param {string} reportType - Type of report
 * @returns {string} CSV formatted data
 */
const formatDateForCSV = (date) => {
    if (!date) return '-';
    const d = date.seconds ? new Date(date.seconds * 1000) : (date.toDate ? date.toDate() : new Date(date));
    return d.toLocaleDateString('tr-TR');
};

export const exportToCSV = (reportData, reportType) => {
    let csvContent = '';

    switch (reportType) {
        case 'production-status':
            csvContent += 'Ürün,Reçete,Miktar,Tamamlanan,Durum,Öncelik,Başlangıç,Bitiş,Atölye,İlerleme\n';
            reportData.orders.forEach(order => {
                csvContent += `"${order.product}","${order.recipe}",${order.quantity},${order.completed},"${order.status}","${order.priority}","${formatDateForCSV(order.startDate)}","${formatDateForCSV(order.endDate)}","${order.assignedTo}",${order.progress}%\n`;
            });
            break;

        case 'raw-material-stock':
            csvContent += 'Hammadde,Kod,Toplam Stok,Birim,Kritik Seviye,Durum\n';
            reportData.materials.forEach(material => {
                csvContent += `"${material.name}","${material.code}",${material.totalStock},"${material.unit}",${material.criticalLevel},"${material.status}"\n`;
            });
            break;

        case 'subcontractor-production':
            csvContent += 'Fasoncu,Toplam Sipariş,Tamamlanan,Devam Eden,Toplam Değer,Üretim Türleri\n';
            reportData.subcontractors.forEach(subcontractor => {
                csvContent += `"${subcontractor.name}",${subcontractor.totalOrders},${subcontractor.completedOrders},${subcontractor.inProgressOrders},${subcontractor.totalOrderValue},"${subcontractor.productionTypes.join(', ')}"\n`;
            });
            break;

        default:
            csvContent += 'Veri yok\n';
    }

    return csvContent;
};

/**
 * Format currency for reports
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted currency
 */
export const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: currency
    }).format(amount);
};

const reportingUtils = {
    generateProductionStatusReport,
    generateRawMaterialStockReport,
    generateSubcontractorProductionReport,
    generateSubcontractorBalanceReport,
    generateProductCostAnalysisReport,
    generateWasteReport,
    exportToCSV,
    formatCurrency
};

export default reportingUtils;
